import React, { useEffect, useState } from "react";
import { Box, Button, Paper, Stack, Switch, TextField, Typography } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectWorkshopDetails } from "../../../app/store/userSlice";
import toast from "react-hot-toast";
import { useUpdateWorkshopMutation } from "../../../app/store/api/workshopApi";
import { useWindowSize } from "../../../shared/hooks/useWindowSize";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const DEFAULT_OPEN = "08:00";
const DEFAULT_CLOSE = "17:00";

const buildInitialState = (openingHours = {}) =>
  DAYS.reduce((acc, day) => {
    const { open, close } = openingHours?.[day] || {};
    const enabled = Boolean(open && close);
    acc[day] = {
      enabled,
      open: open || DEFAULT_OPEN,
      close: close || DEFAULT_CLOSE,
    };
    return acc;
  }, {});

const WorkshopOpenDayAndTime = () => {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const workshopDetails = useSelector(selectWorkshopDetails);

  const [hours, setHours] = useState(() => buildInitialState(workshopDetails?.openingHours));
  const [updateWorkshop, { isLoading: saving }] = useUpdateWorkshopMutation();

  const isDirty = JSON.stringify(hours) !== JSON.stringify(buildInitialState(workshopDetails?.openingHours));

  useEffect(() => {
    setHours(buildInitialState(workshopDetails?.openingHours));
  }, [workshopDetails?.openingHours]);

  const toggleDay = (day) => setHours((prev) => ({ ...prev, [day]: { ...prev[day], enabled: !prev[day].enabled } }));

  const changeTime = (day, key, value) => setHours((prev) => ({ ...prev, [day]: { ...prev[day], [key]: value } }));

  const isInvalid = (d) => d.enabled && (!d.open || !d.close || d.close <= d.open);
  const hasErrors = DAYS.some((day) => isInvalid(hours[day]));

  const handleSave = async () => {
    if (hasErrors) return;

    const daysData = DAYS.reduce((acc, day) => {
      const d = hours[day];
      acc[day] = d.enabled ? { open: d.open, close: d.close } : { open: null, close: null };
      return acc;
    }, {});

    const payload = { openingHours: daysData };

    toast.promise(
      async () => {
        await updateWorkshop({ workshopId: workshopDetails.id, payload }).unwrap();
      },
      {
        success: t("toastMessage.saveSuccess"),
        loading: t("toastMessage.saveLoading"),
        error: t("toastMessage.saveError"),
      },
    );
  };

  return (
    <Paper variant='outlined' sx={{ borderColor: "#e3e6ec", borderRadius: 2, p: 3, mb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 700, color: "#003C59", lineHeight: 1.3 }}>
          {t("workshopOpeningHours.openingHours")}
        </Typography>
      </Box>

      <Stack spacing={1.5}>
        {DAYS.map((day, idx) => {
          const d = hours[day];
          const invalid = isInvalid(d);

          return isMobile ? (
            <Box
              key={day}
              spacing={2}
              sx={{
                py: 1,
                borderBottom: idx < DAYS.length - 1 ? "1px solid #e3e6ec" : "none",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1, justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Switch
                    checked={d.enabled}
                    onChange={() => toggleDay(day)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#003C59" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#003C59" },
                    }}
                  />
                  <Box sx={{ width: 130 }}>
                    <Typography variant='body2' sx={{ fontWeight: 400, color: "text.primary", lineHeight: 1.3 }}>
                      {t(`workshopOpeningHours.${day}`)}
                    </Typography>
                  </Box>
                </Box>
                {!d.enabled && (
                  <Box>
                    <Typography variant='body2' sx={{ fontWeight: 400, color: "text.primary", lineHeight: 1.3, fontStyle: "italic" }}>
                      {t("workshopSettings.closed")}
                    </Typography>
                  </Box>
                )}
              </Box>

              {d.enabled && (
                <Stack direction='row' spacing={1} alignItems='center' sx={{ flex: 1, justifyContent: "center" }}>
                  <TextField size='small' type='time' value={d.open} onChange={(e) => changeTime(day, "open", e.target.value)} error={invalid} inputProps={{ step: 300 }} sx={{ width: 120 }} />
                  <Typography sx={{ color: "#8a94a6" }}>–</Typography>
                  <TextField
                    size='small'
                    type='time'
                    value={d.close}
                    onChange={(e) => changeTime(day, "close", e.target.value)}
                    error={invalid}
                    helperText={invalid ? t("workshopSettings.invalidTimeRange", "Closing must be after opening") : ""}
                    inputProps={{ step: 300 }}
                    sx={{ width: 120 }}
                  />
                </Stack>
              )}
            </Box>
          ) : (
            <Stack
              key={day}
              direction='row'
              alignItems='center'
              spacing={2}
              sx={{
                py: 1,
                borderBottom: idx < DAYS.length - 1 ? "1px solid #e3e6ec" : "none",
              }}
            >
              <Switch
                checked={d.enabled}
                onChange={() => toggleDay(day)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#003C59" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#003C59" },
                }}
              />
              <Box sx={{ width: 130 }}>
                <Typography variant='body2' sx={{ fontWeight: 400, color: "text.primary", lineHeight: 1.3 }}>
                  {t(`workshopOpeningHours.${day}`)}
                </Typography>
              </Box>

              {d.enabled ? (
                <Stack direction='row' spacing={1} alignItems='center' sx={{ flex: 1 }}>
                  <TextField size='small' type='time' value={d.open} onChange={(e) => changeTime(day, "open", e.target.value)} error={invalid} inputProps={{ step: 300 }} sx={{ width: 120 }} />
                  <Typography sx={{ color: "#8a94a6" }}>–</Typography>
                  <TextField
                    size='small'
                    type='time'
                    value={d.close}
                    onChange={(e) => changeTime(day, "close", e.target.value)}
                    error={invalid}
                    helperText={invalid ? t("workshopSettings.invalidTimeRange", "Closing must be after opening") : ""}
                    inputProps={{ step: 300 }}
                    sx={{ width: 120 }}
                  />
                </Stack>
              ) : (
                <Box>
                  <Typography variant='body2' sx={{ fontWeight: 400, color: "text.primary", lineHeight: 1.3, fontStyle: "italic" }}>
                    {t("workshopSettings.closed")}
                  </Typography>
                </Box>
              )}
            </Stack>
          );
        })}
      </Stack>

      <Button variant='contained' startIcon={<SaveIcon fontSize='small' />} onClick={handleSave} disabled={saving || hasErrors || !isDirty} sx={{ mt: 3, bgcolor: "#003C59", "&:hover": { bgcolor: "#003C59" } }}>
        {t("workshopSettings.save")}
      </Button>
    </Paper>
  );
};

export default WorkshopOpenDayAndTime;
